import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import scrollToComponent from 'react-scroll-to-component';
import { Redirect } from 'react-router-dom';

import EventOnlineView from '../components/content/event/EventOnlineView';
import Faq from '../components/content/Faq';
import LoadingView from '../components/content/LoadingView';
import MediaCollectionList from '../components/content/lists/MediaCollectionList';
import TimeSlider from '../components/content/extended-search/TimeSlider';
import withRequestHandler from '../components/hoc/withRequestHandler';

import { ALL_PHOTOS_CATEGORY } from '../utils/variables';

class EventCategorySearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      extendedSearchSelectedTab: 1,
      extendedSearchExpand: true,
      categoryId: null,
    };
    this._refetchMediaForNewCategory = this._refetchMediaForNewCategory.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { categoryIdFromUrl, viewer } = nextProps;
    let mediaInfo = null;
    if (viewer.event.media) {
      if (categoryIdFromUrl === ALL_PHOTOS_CATEGORY) {
        mediaInfo = viewer.event.media.mediaInfo;
      }
    } else {
      if (categoryIdFromUrl === viewer.event.mediaCollection.id) {
        mediaInfo = viewer.event.mediaCollection.media.mediaInfo;
      }
    }

    if (mediaInfo && categoryIdFromUrl !== prevState.categoryId) {
      return {
        categoryId: categoryIdFromUrl,
        minValue: new Date(mediaInfo.shotDateMin).getTime(),
        maxValue: new Date(mediaInfo.shotDateMax).getTime(),
        inputRangeValue: {
          min: new Date(mediaInfo.shotDateMin).getTime(),
          max: new Date(mediaInfo.shotDateMax).getTime(),
        },
      };
    }
    return null;
  }

  componentDidUpdate(prevProps) {
    const { categoryIdFromUrl } = this.props;
    if (categoryIdFromUrl !== prevProps.categoryIdFromUrl) {
      scrollToComponent(this.timeSliderDiv, { align: 'top' });
      this._refetchMediaForNewCategory(categoryIdFromUrl);
    }
  }

  componentDidMount() {
    scrollToComponent(this.timeSliderDiv, { align: 'top' });
  }

  handleExtendedSearchTabChanged = (event, value) => {
    this.setState({
      extendedSearchSelectedTab: value,
      extendedSearchExpand: true,
    });
  };

  handleExtendedSearchExpand = () => {
    this.setState(prevState => {
      const {
        extendedSearchExpand: prevExpand,
        extendedSearchSelectedTab: prevSelectedTab,
      } = prevState;

      return {
        extendedSearchExpand: !prevExpand,
        // if the extended search changes from open to close it is important to reset
        // the selected tab to its default. Otherwise the media list will not be rendered.
        extendedSearchSelectedTab: prevExpand ? 1 : prevSelectedTab,
      };
    });
  };

  handleInputRangeValueChanged = value => {
    this.setState({ inputRangeValue: value });
  };

  handleInputRangeChangeCompleted = value => {
    this._refetchMediaForNewTimeRange(value);
  };

  scrollToEventOnlineView(tabIndex) {
    scrollToComponent(this.eventOnlineView);
    this.setState({
      extendedSearchExpand: true,
      extendedSearchSelectedTab: tabIndex,
    });
  }

  _refetchMediaForNewCategory(newCategoryId) {
    this.props.onRequestStart();
    this.props.relay.refetch(
      {
        categoryId: newCategoryId,
        fetchCategory: newCategoryId !== ALL_PHOTOS_CATEGORY,
        fetchAllPhotos: newCategoryId === ALL_PHOTOS_CATEGORY,
      },
      null,
      () => {
        this.props.onRequestStop();
      }
    );
  }

  _refetchMediaForNewTimeRange = newTimeRange => {
    this.props.onRequestStart();
    const { categoryId } = this.state;
    this.props.relay.refetch(
      {
        categoryId: categoryId,
        fetchCategory: categoryId !== ALL_PHOTOS_CATEGORY,
        fetchAllPhotos: categoryId === ALL_PHOTOS_CATEGORY,
        minTime: new Date(new Date(newTimeRange.min).setSeconds(0)).toISOString(),
        maxTime: new Date(new Date(newTimeRange.max).setSeconds(59)).toISOString(),
      },
      null,
      () => {
        this.props.onRequestStop();
      }
    );
  };

  render() {
    const {
      isLoading,
      snFromUrl,
      categoryIdFromUrl,
      eventIdFromUrl,
      viewer,
      customerKey,
    } = this.props;

    const { event } = viewer;

    if (event.state === 'reminder') {
      return <Redirect to={`/event/${event.sgId}`} />;
    }

    const participantMediaCount = event.participant
      ? event.participant.media.mediaInfo.count
      : 0;
    if (categoryIdFromUrl === ALL_PHOTOS_CATEGORY && !event.searchAllPhotos) {
      // If the search for all photos of the event is disabled by the backend but the
      // user tries to navigate to this category via the URL, the user should be redirected
      // to event page
      return <Redirect to={`/event/${event.sgId}`} />;
    }

    const {
      minValue,
      maxValue,
      inputRangeValue,
      extendedSearchSelectedTab,
      extendedSearchExpand,
    } = this.state;

    return (
      <div className="container-960 container-page">
        <EventOnlineView
          ref={view => (this.eventOnlineView = view)}
          onlineEvent={event}
          urlSn={snFromUrl}
          customerKey={customerKey}
          extendedSearchSelectedTab={extendedSearchSelectedTab}
          extendedSearchExpand={extendedSearchExpand}
          extendedSearchSelectedCategoryId={categoryIdFromUrl}
          hasSmartSearchTab={event.smartSearch && participantMediaCount !== 0}
          hasGpsSearchTab={event.gps}
          hasFaceSearchTab={event.searchByFaceAsFeature && (!event.searchByFace || participantMediaCount !== 0)}
          onExtendedSearchTabChange={this.handleExtendedSearchTabChanged}
          onExtendedSearchExpand={this.handleExtendedSearchExpand}
        />
        {extendedSearchSelectedTab === 1 && extendedSearchExpand && (
          <div ref={div => (this.timeSliderDiv = div)}>
            <TimeSlider
              minValue={minValue}
              maxValue={maxValue}
              value={inputRangeValue}
              onChange={this.handleInputRangeValueChanged}
              onChangeComplete={this.handleInputRangeChangeCompleted}
            />
          </div>
        )}
        <hr className="col-xs-16 col-sm-12" />
        {isLoading && <LoadingView />}
        {!isLoading && extendedSearchSelectedTab === 1 && (
          <MediaCollectionList
            eventHasSearchByFace={event.searchByFace ? true : false}
            eventHasSecondFactor={event.secondFactorType ? true : false}
            eventId={eventIdFromUrl}
            categoryId={categoryIdFromUrl}
            startnumber={snFromUrl}
            mediaCollectionMedia={event.media || event.mediaCollection.media}
            offer={event}
          />
        )}
        <Faq onFindMore={() => this.scrollToEventOnlineView(0)} />
      </div>
    );
  }
}

EventCategorySearchPage.propTypes = {
  customerKey: PropTypes.string,
  isLoading: PropTypes.bool,
  snFromUrl: PropTypes.string,
  categoryIdFromUrl: PropTypes.string,
  eventIdFromUrl: PropTypes.string,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
};

export { EventCategorySearchPage };

export default createRefetchContainer(
  withRequestHandler(EventCategorySearchPage),
  {
    viewer: graphql`
      fragment EventCategorySearchPage_viewer on Viewer {
        event(id: $eventId, language: $language) {
          id
          sgId
          gps
          hostResultUrl

          media @include(if: $fetchAllPhotos) {
            mediaInfo {
              shotDateMax
              shotDateMin
            }
            ...MediaCollectionList_mediaCollectionMedia
              @arguments(fetchAllPhotos: $fetchAllPhotos, fetchCategory: $fetchCategory)
          }

          mediaCollection(id: $categoryId) @include(if: $fetchCategory) {
            id
            media {
              mediaInfo {
                shotDateMax
                shotDateMin
              }
              ...MediaCollectionList_mediaCollectionMedia
                @arguments(fetchAllPhotos: $fetchAllPhotos, fetchCategory: $fetchCategory)
            }
          }

          participant(startnumber: $startnumber, secondFactor: $secondFactor) @skip(if: $noStartnumber){
            media {
              mediaInfo {
                count
              }
            }
            startnumber
          }
          searchAllPhotos
          secondFactorType {
            type
          }
          searchByFace
          searchByFaceAsFeature
          smartSearch
          state
          ...EventOnlineView_onlineEvent
          ...MediaCollectionList_offer
        }
      }
    `,
  },
  graphql`
    query EventCategorySearchPageRefetchQuery(
      $eventId: ID!
      $language: String
      $startnumber: ID!
      $categoryId: ID!
      $count: Int!
      $cursor: String
      $secondFactor: String
      $individualPart: String
      $noStartnumber: Boolean
      $fetchCategory: Boolean
      $fetchAllPhotos: Boolean
      $minTime: NaiveDatetime
      $maxTime: NaiveDatetime
    ) {
      viewer {
        ...EventCategorySearchPage_viewer
      }
    }
  `
);
